import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../../types'
import {
    BASE_HORIZONTAL_PADDING,
    CENTER_COLUMN_PINNED_RANGES,
    CENTER_ROW_PINNED_RANGES,
    CORNER_PINNED_RANGES,
    HEADER_COMPONENTS_WIDTH,
    LOGO_RATIOS
} from '../constants'
import {SemanticTypes} from '../../../preprocess/conversionDataBuilders'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getTargetLayoutPointer
} from '../../../conversionDal/utils/pointer'
import {
    getParent,
    isChildOfHeader,
    isHeaderContainer,
    isHeaderFirstRowChildComponentSpecialCase,
    isBoxPlugin,
    isInternalBox,
    isMenuContainerChild,
    isPinnedToContainer,
    isPlugin,
    isSection,
    isSectionsRootContainer,
    isCssGrid,
    isSuperGrid,
    isSuperGridCell,
    predicatePinnedHeuristic,
    shouldBeHorizontallyStretched,
    updateTargetLayout
} from '../../common'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {Orientation} from '../../../enums'
import {VIEW_MODES} from '../../../../../constants/constants'
import {pointerUtils} from '@wix/document-manager-core'
import {
    getFallbackRatio,
    isInRange,
    getContentBoxKey,
    getHorizontalPadding,
    setHorizontalPadding,
    writeHorizontalMarginToContext,
    updateHorizontalMargins,
    getMaxWidth,
    updateComponentWidth
} from '../common'

const setMaxContentWidth = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    const maxWidth = getMaxWidth(componentStageData, contextHelper)
    const horizontalPadding = getHorizontalPadding(contextHelper, componentPointer)
    contextHelper.set(getContentBoxKey(componentPointer), maxWidth - horizontalPadding)

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                type: 'maxContent'
            },
            type: 'ComponentLayout'
        }
    })
}

const updatePinnedHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer, config} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right

    const relativeSize = config.targetCanvasSize
    if (!target) target = (marginToUpdate.value * config.sourceCanvasSize) / 100
    marginToUpdate.value = (target / relativeSize) * 100

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'FixedItemLayout'
        })
    })

    const totalMargin = (marginToUpdate.value * relativeSize) / 100 + BASE_HORIZONTAL_PADDING
    writeHorizontalMarginToContext(contextHelper, componentPointer, totalMargin)
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right

    const relativeSize = contextHelper.get(getContentBoxKey(getParent(conversionDal, componentPointer)!))
    const parentDesktopWidth = initialSnapshot.getValue(
        getMeasurementsPointer(getParent(conversionDal, componentPointer)!)
    ).width

    if (!target) target = (marginToUpdate.value * parentDesktopWidth) / 100
    marginToUpdate.value = (target / relativeSize) * 100

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'GridItemLayout'
        })
    })

    const totalMargin = (marginToUpdate.value * relativeSize) / 100 + BASE_HORIZONTAL_PADDING
    writeHorizontalMarginToContext(contextHelper, componentPointer, totalMargin)
}

const updateGridContainerHorizontalPadding = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    paddingLeft: number,
    paddingRight: number,
    columnGap: number
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

    _.set(containerLayout, ['padding', 'left'], {
        value: paddingLeft,
        type: 'spx'
    })

    _.set(containerLayout, ['padding', 'right'], {
        value: paddingRight,
        type: 'spx'
    })

    _.set(containerLayout, ['columnGap'], {
        value: columnGap,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })

    const totalPadding = paddingLeft + paddingRight
    setHorizontalPadding(contextHelper, componentPointer, totalPadding)
}

const updateHeaderContainerHorizontalPadding = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext
) => {
    const padding = HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
    const columnGap = HEADER_COMPONENTS_WIDTH.HEADER_COMPONENT_GAP

    updateGridContainerHorizontalPadding(componentStageData, contextHelper, padding, padding, columnGap)
}

const updateGridHorizontalPadding = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {conversionDal, componentPointer} = componentStageData
    const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
    const sourcePadding = sourceLayout?.containerLayout?.padding

    _.set(containerLayout, ['padding', 'left'], {
        value: sourcePadding?.left?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['padding', 'right'], {
        value: sourcePadding?.right?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['columnGap'], {
        value: sourceLayout?.containerLayout?.columnGap?.value ? 24 : 0,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })

    const totalPadding = (containerLayout.padding?.left?.value || 0) + (containerLayout.padding?.right?.value || 0)
    setHorizontalPadding(contextHelper, componentPointer, totalPadding)
}

const resetHorizontalPadding = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
    _.set(containerLayout, ['padding', 'left'], {value: 0, type: 'spx'})
    _.set(containerLayout, ['padding', 'right'], {value: 0, type: 'spx'})

    updateTargetLayout(componentStageData, {
        containerLayout
    })

    setHorizontalPadding(contextHelper, componentPointer, 0)
    writeHorizontalMarginToContext(contextHelper, componentPointer, 0)
}

export const horizontalSpacingHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: ({componentPointer}: ComponentStageData, contextHelper: PluginContext) =>
            writeHorizontalMarginToContext(contextHelper, componentPointer, 0),
        heuristicCode: 'SECTION X'
    },
    ...CORNER_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                (justifySelf, alignSelf) => alignSelf !== 'center' && justifySelf !== 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CORNER PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_COLUMN_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                justifySelf => justifySelf === 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER COLUMNED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_ROW_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                (justifySelf, alignSelf) => alignSelf === 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER ROWED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isHeaderContainer({componentPointer, conversionDal}),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHeaderContainerHorizontalPadding(componentStageData, contextHelper),
        heuristicCode: 'HEADER CONTAINER HORIZONTAL PADDING'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isChildOfHeader(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0),
        heuristicCode: 'COMPONENT IN HEADER'
    },
    {
        predicate: isSuperGrid,
        apply: updateGridHorizontalPadding,
        heuristicCode: 'SUPERGRID HORIZONTAL PADDING AND COLUMN GAP'
    },
    {
        predicate: isCssGrid,
        apply: updateGridHorizontalPadding,
        heuristicCode: 'CSSGRID HORIZONTAL PADDING AND COLUMN GAP'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isInternalBox(componentStageData) || isBoxPlugin(componentStageData),
        apply: resetHorizontalPadding,
        heuristicCode: 'RESET HORIZONTAL PADDING FOR INTERNAL BOX'
    },
    {
        predicate: isPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            writeHorizontalMarginToContext(contextHelper, componentPointer, 0)
        },
        heuristicCode: 'BOX PLUGIN'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: '0 HORIZONTAL MARGINS FOR SUPERGRID CELL'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.BOX) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({
                    conversionDal,
                    componentPointer: parentPointer!
                })
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: 'BOX WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.IMAGE) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({
                    conversionDal,
                    componentPointer: parentPointer!
                })
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: 'IMAGE WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: shouldBeHorizontallyStretched,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: 'KEEP HORIZONTALLY STRETCHED'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, BASE_HORIZONTAL_PADDING),
        heuristicCode: 'FALLBACK X'
    }
]

export const widthHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (
            {componentPointer, config}: ComponentStageData,
            _contextHelper?: PluginContext,
            readOnlyExtensionApi?: ReadOnlyExtensionAPI
        ) =>
            !!readOnlyExtensionApi?.layout.isContentWidth(
                pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP, {
                    variants: config.targetVariants
                })
            ),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            setMaxContentWidth(componentStageData, contextHelper)
    },
    ...LOGO_RATIOS.map(({min, max, target}) => ({
        predicate: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => {
            const {componentPointer, conversionDal, globalContext} = componentStageData

            if (
                !isHeaderFirstRowChildComponentSpecialCase(
                    componentPointer,
                    conversionDal,
                    globalContext,
                    readOnlyExtensionAPI
                )
            )
                return false
            const component = conversionDal.get(componentPointer)
            const {componentLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
            const ratio =
                componentLayout?.height?.type === 'aspectRatio'
                    ? componentLayout.height.value
                    : getFallbackRatio(componentStageData)
            return component.semanticType === SemanticTypes.LOGO && isInRange(ratio, min, max)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, target, target),
        heuristicCode: `FIRST ROW HEADER LOGO WITH ASPECT RATIO WIDTH ${target}`
    })),
    {
        predicate: (
            {componentPointer, conversionDal, globalContext}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => {
            return isHeaderFirstRowChildComponentSpecialCase(
                componentPointer,
                conversionDal,
                globalContext,
                readOnlyExtensionAPI
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {conversionDal, componentPointer} = componentStageData
            const width = conversionDal.get(getMeasurementsPointer(componentPointer))?.width
            updateComponentWidth(componentStageData, contextHelper, width, width)
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW  SEPCIAL CASE'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.APP &&
            isChildOfHeader(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.TPA_WIDTH),
        heuristicCode: 'TPA IN HEADER WIDTH'
    },
    ...LOGO_RATIOS.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const {componentLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
            const ratio =
                componentLayout?.height?.type === 'aspectRatio'
                    ? componentLayout.height.value
                    : getFallbackRatio(componentStageData)
            return component.semanticType === SemanticTypes.LOGO && isInRange(ratio, min, max)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, target),
        heuristicCode: `LOGO WITH ASPECT RATIO WIDTH ${target}`
    })),
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            if (!originalMeasurements?.width) {
                return
            }

            const maxWidth = getMaxWidth(componentStageData, contextHelper)

            if (!maxWidth) {
                return
            }

            const width = Math.min(originalMeasurements.width, maxWidth)
            updateComponentWidth(componentStageData, contextHelper, width, maxWidth)
        },
        heuristicCode: 'MENU CONTAINER CHILD PROPORTIONAL WIDTH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: ({componentPointer, config}: ComponentStageData, contextHelper: PluginContext) =>
            contextHelper.set(getContentBoxKey(componentPointer), config.targetCanvasSize)
    },
    {
        predicate: isPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'STRETCH PLUGIN IN PLACEHOLDER'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'BOX WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.IMAGE) {
                return false
            }
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({conversionDal, componentPointer: parentPointer!})
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            updateComponentWidth(componentStageData, contextHelper, maxWidth)
        },
        heuristicCode: 'IMAGE WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(componentStageData, contextHelper, Math.min(originalMeasurements.width, maxWidth))
        },
        heuristicCode: 'NARROW IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG &&
                originalMeasurements.width <= 100
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(componentStageData, contextHelper, Math.min(originalMeasurements.width, maxWidth))
        },
        heuristicCode: 'NARROW SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width * 0.7, maxWidth)
            )
        },
        heuristicCode: 'SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.TEXT,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'TEXT WIDTH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, conversionDal, componentPointer} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(componentStageData, contextHelper, Math.min(originalMeasurements.width, maxWidth))
        },
        heuristicCode: 'NARROW HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BUTTON,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const maxWidth = getMaxWidth(componentStageData, contextHelper)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            updateComponentWidth(componentStageData, contextHelper, Math.min(originalMeasurements.width, maxWidth))
        },
        heuristicCode: 'BUTTON WIDTH'
    },
    {
        predicate: shouldBeHorizontallyStretched,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'KEEP HORIZONTALLY STRETCHED WIDTH'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(componentStageData, contextHelper, getMaxWidth(componentStageData, contextHelper))
        },
        heuristicCode: 'FALLBACK WIDTH'
    }
]
